<template>
  <div>
    <div v-if="parsedAvailabilities.length" class="my-5">
      <div class="products-list">
        <div
          v-for="(room, idx) in parsedAvailabilities"
          :key="`${room.roomType}-${idx}`"
          class="mx-0 products-list-item my-5"
        >
          <div class="row p-md-3 px-0 py-3">
            <div class="col-md-5">
              <div class="position-relative">
                <b-carousel
                  id="carousel-fade"
                  :interval="3000"
                  style="text-shadow: 0px 0px 2px #000"
                  fade
                  indicators
                  img-width="1024"
                  img-height="480"
                  ><div
                    class="cursor-pointer"
                    @click="$emit('openDetails', room.images)"
                  >
                    <template v-if="room.images.length">
                      <div v-for="url of room.images" :key="url">
                        <b-carousel-slide
                          style="max-height: 250px"
                          :img-src="url"
                        ></b-carousel-slide>
                      </div>
                    </template>
                    <template v-else>
                      <b-carousel-slide
                        v-for="i of 1"
                        :key="i"
                        img-src="/images/property-default-image.jpg"
                      ></b-carousel-slide>
                    </template>
                  </div>
                </b-carousel>
              </div>
            </div>
            <div class="col-md-7">
              <h4
                class="mb-md-3 mb-1 text-main text-capitalize font-weight-bold mt-md-0 mt-3"
              >
                {{ room.roomType }}
              </h4>
              <div
                class="product-desc"
                v-html="displayedContent(room.availabilities)"
              ></div>
              <span
                v-if="showToggle(room.availabilities)"
                @click="showAll = !showAll"
                class="text-main cursor-pointer small"
                ><u>{{
                  $t(`property.${showAll ? "showLess" : "showMore"}`)
                }}</u></span
              >
            </div>
          </div>
          <div
            v-for="availability in room.availabilities"
            :key="availability.id"
            class="selectors p-md-3 px-0 py-2"
          >
            <div class="row">
              <div class="col-md-5">
                <div
                  class="small"
                  :class="`text-${
                    availability.breakfast ? 'success' : 'danger'
                  }`"
                >
                  <img
                    v-if="availability.breakfast"
                    src="../../assets/icons/meal-success.svg"
                    class="mr-1"
                    alt="meal"
                  />
                  <img
                    v-else
                    src="../../assets/icons/meal.svg"
                    class="mr-1"
                    alt="meal"
                  />
                  <span>{{
                    availability.breakfast
                      ? $t("reservation.breakfastIncluded")
                      : $t("property.noMealIncluded")
                  }}</span>
                </div>
                <div class="mt-2 font-weight-bold">
                  {{
                    availability.numberOfRoomsAvailable > 1
                      ? `${availability.numberOfRoomsAvailable}x`
                      : ""
                  }}
                  {{ rateDetails(availability) }}
                </div>
                <div class="small text-main mt-2">
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      rateDetailsText(availability)
                    "
                    >{{ $t("property.rateDetails") }}</span
                  >
                  |
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      cancellationPolicyText(availability)
                    "
                    >{{ $t("property.cancellationPolicy") }}</span
                  >
                </div>
              </div>
              <div class="col-md-3">
                <div class="h-100 d-md-flex d-block">
                  <div class="my-md-auto my-1">
                    <GuestIcons :persons="availability.maxPersons" />
                    <span class="ml-1 text-14">{{
                      $tc("property.personsCount", availability.maxPersons)
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div
                  class="h-100 mt-md-auto mt-0 d-flex align-items-center justify-content-between"
                >
                  <div>
                    <h3 class="text-main font-weight-bold actual-amount">
                      {{ availability.currencyCode }}
                      {{
                        (
                          availability.amount *
                          availability.numberOfRoomsAvailable
                        ).toFixed(2) | formatPrice
                      }}
                    </h3>
                  </div>
                  <div>
                    <button
                      @click="bookNow(availability)"
                      class="btn px-4 g-primary-button"
                    >
                      {{ $t("property.bookNow") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="fetched && !availabilities.length"
      class="text-danger no-availability py-3 my-5 text-center"
    >
      <h5 class="mb-0">
        <b-icon icon="exclamation-circle" class="text-danger"></b-icon>
        <span class="ml-2">
          <span v-html="noAvailabilityMessage"></span>
        </span>
      </h5>
    </div>
    <div v-else-if="!fetched" class="py-5 mt-5 text-center text-md">
      <h5>{{ $t("property.selectStay") }}</h5>
    </div>
  </div>
</template>

<script>
import {
  rateDetails,
  productName,
  rateDetailsText,
  cancellationPolicyText,
} from "../../helpers/ehotel";
import { mapState } from "vuex";
import htmlTruncate from "html-truncate";
import GuestIcons from "../Widgets/GuestIcons.vue";
import {
  getNoAvailabilityMessage,
  getPropertyPrimaryEmail,
} from "../../helpers/theme";

export default {
  name: "EhotelList",
  components: { GuestIcons },
  props: {
    property: {
      type: Object,
      required: true,
    },
    availabilities: {
      type: Array,
      required: true,
    },
    fetched: {
      type: Boolean,
      default: false,
    },
    checkIn: {
      type: String,
      default: null,
    },
    checkOut: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      cart: [],
      showAll: false,
      maxChars: 105,
    };
  },
  computed: {
    ...mapState("property", ["availabiltyDefaultMedia"]),
    noAvailabilityMessage() {
      return getNoAvailabilityMessage({
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        hotelEmail: getPropertyPrimaryEmail(this.property),
      });
    },
    parsedAvailabilities() {
      const rooms = Array.isArray(this.availabilities) ? this.availabilities : [];
      return rooms.map((availability) => {
        if (
          !availability.images.length &&
          this.availabiltyDefaultMedia.length
        ) {
          availability.images = this.availabiltyDefaultMedia;
        }
        return availability;
      });
    },
  },
  methods: {
    productName,
    rateDetails,
    rateDetailsText,
    cancellationPolicyText,
    showToggle(availabilities) {
      const html = this.displayedContent(availabilities);
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = div.textContent || div.innerText || "";
      return text.length > this.maxChars;
    },
    displayedContent(availabilities) {
      const description = [...this.availabilityDesc(availabilities)].join(
        "<br>"
      );

      if (this.showAll) {
        return description;
      }

      return htmlTruncate(description, this.maxChars, { ellipsis: `...` });
    },
    availabilityDesc(availabilities) {
      const texts = new Set();
      for (const availability of availabilities || []) {
        const text = availability.descriptions?.length
          ? availability.descriptions[0]?.text
          : "";

        if (text) {
          texts.add(text.trim());
        }
      }

      return Array.from(texts);
    },
    bookNow(availability) {
      this.$emit("bookNow", {
        id: availability.id,
        quantity: availability.numberOfRoomsAvailable,
        guests: availability.maxPersons,
        price: availability.amount,
        currencyCode: availability.currencyCode,
        product: availability,
      });
    },
  },
};
</script>

<style>
.text-14 {
  font-size: 14px;
}
.actual-amount {
  font-size: 28px;
}

@media screen and (max-width: 600px) {
  .actual-amount {
    font-size: 20px;
    margin-bottom: 0;
  }
  .text-14 {
    font-size: 12px;
  }
}
</style>
